import { BatchReportResponse } from 'compliance-api';
import { FormikProvider, useFormik } from 'formik';
import { useCallback } from 'react';
import * as yup from 'yup';
import ReportTemplatesSelectPaginate from '@/modules/admin-dashboard/components/ReportTemplatesSelectPaginate.tsx';
import {
  useCreateReportsBatchMutation,
  useUpdateReportsBatchMutation,
} from '@/modules/admin-dashboard/queries/reports-batch.ts';
import {
  BaseModalProps,
  Button,
  ModalContentWrapper,
  SelectPaginateOption,
  TextInputField,
} from 'ui';

const initialValues = {
  name: '',
  templates: [] as SelectPaginateOption<number>[],
};

interface BatchReportsModalProps extends BaseModalProps {
  initBatch?: BatchReportResponse;
  onSuccess?: (id: number, name: string) => void;
}

const validationSchema = yup.object({
  name: yup.string().min(3).max(100).required('Please write some name'),
  templates: yup
    .array()
    .min(1, 'At least one template is required')
    .max(100, 'Too many templates')
    .required('At least one template is required'),
});

const BatchReportsModal = ({ open, initBatch, onClose, onSuccess }: BatchReportsModalProps) => {
  const { mutateAsync: createBatch, isLoading: isCreating } = useCreateReportsBatchMutation();
  const { mutateAsync: updateBatch, isLoading: isUpdating } = useUpdateReportsBatchMutation();

  const formik = useFormik({
    validationSchema,
    initialValues: initBatch
      ? {
          name: initBatch.name,
          templates: (initBatch.templates || []).map(template => ({
            label: template.name,
            value: template.id,
          })),
        }
      : initialValues,
    onSubmit: async values => {
      const adaptedValues = {
        name: values.name,
        report_template_ids: values.templates.map(({ value }) => value),
      };

      if (initBatch) {
        await updateBatch({ id: initBatch.id, ...adaptedValues });
      } else {
        const newBatch = await createBatch(adaptedValues);
        if (newBatch.batch_report.id) {
          onSuccess?.(newBatch.batch_report.id, values.name);
        }
      }
      onClose();
    },
  });

  const handleChangeTemplates = useCallback(
    (options: SelectPaginateOption<number>[]) => {
      formik.setFieldValue('templates', options);
    },
    [formik],
  );
  return (
    <ModalContentWrapper
      title={initBatch ? 'Update batch' : 'Create new batch'}
      open={open}
      onClose={onClose}
    >
      <FormikProvider value={formik}>
        <div>
          <TextInputField name="name" label="batch name" placeholder="Name..." />
          <ReportTemplatesSelectPaginate
            enableCreation
            label="templates"
            isMulti={true}
            onChange={handleChangeTemplates}
            className="mt-8"
            value={formik.values.templates}
            error={
              Array.isArray(formik.errors.templates)
                ? formik.errors.templates.toString()
                : formik.errors.templates
            }
          />
          <Button
            loading={isCreating || isUpdating}
            disabled={isCreating || isUpdating}
            className="mt-10"
            onClick={formik.submitForm}
          >
            {initBatch ? 'Update' : 'Create'}
          </Button>
        </div>
      </FormikProvider>
    </ModalContentWrapper>
  );
};

export default BatchReportsModal;
